import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { FaCheckCircle, FaClock, FaEye, FaHourglassHalf, FaUser } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_URL;


function JBranchAuditOverview() {
  const [allChecklists, setAllChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    total: 0
  });
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_API_URL;

  // Set initial tab based on URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    
    if (statusParam && ['pending', 'inProgress', 'completed'].includes(statusParam)) {
      setActiveTab(statusParam);
    } else {
      setActiveTab('all');
    }
  }, [location.search]);

  // Update URL when tab changes
  const handleTabSelect = (selectedTab) => {
    setActiveTab(selectedTab);
    
    // Update URL without page reload
    const searchParams = new URLSearchParams(location.search);
    if (selectedTab === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', selectedTab);
    }
    
    const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    navigate(newUrl, { replace: true });
  };

  // Get status icon based on the status
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return <FaClock className="me-1" size={14} />;
      case 'in progress': return <FaHourglassHalf className="me-1" size={14} />;
      case 'completed': return <FaCheckCircle className="me-1" size={14} />;
      default: return null;
    }
  };

  // Get status color based on the status
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'in progress': return 'primary';
      case 'completed': return 'success';
      default: return 'secondary';
    }
  };

  useEffect(() => {
    const fetchAllChecklists = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        
        if (!userData || !userData.BranchCode) {
          throw new Error('Admin data not found');
        }

        // Fetch checklists for all statuses
        const statuses = ['pending', 'inProgress', 'completed'];
        const promises = statuses.map(async (status) => {
          try {
            const response = await fetch(`${BASE_URL}/get-checklists-by-status/${userData.BranchCode}/${status}`);
            if (response.ok) {
              const data = await response.json();
              return { status, checklists: data.checklists || [] };
            }
            return { status, checklists: [] };
          } catch (err) {
            console.error(`Error fetching ${status} checklists:`, err);
            return { status, checklists: [] };
          }
        });

        const results = await Promise.all(promises);
        
        // Combine all checklists and add progress data
        let combinedChecklists = [];
        let statusStats = { pending: 0, inProgress: 0, completed: 0, total: 0 };

        for (const result of results) {
          const { status, checklists } = result;
          
          // Count for stats
          if (status === 'pending') statusStats.pending = checklists.length;
          if (status === 'inProgress') statusStats.inProgress = checklists.length;
          if (status === 'completed') statusStats.completed = checklists.length;
          
          // Fetch progress for each checklist using assigned checklist ID
          const checklistsWithProgress = await Promise.all(
            checklists.map(async (checklist) => {
              try {
                // Updated to use assigned checklist ID (checklist.id) instead of checklist.checklistId
                const progressResponse = await fetch(
                  `${BASE_URL}/get-jbranch-checklist-progress-status/${checklist.id}/${userData.BranchCode}/${checklist.branchId}`
                );
                
                if (progressResponse.ok) {
                  const progressData = await progressResponse.json();
                  return {
                    ...checklist,
                    progressPercentage: progressData.progressPercentage || 0,
                    totalQuestions: progressData.totalQuestions || 0,
                    answeredQuestions: progressData.answeredQuestions || 0,
                    remainingQuestions: progressData.remainingQuestions || 0,
                    actualChecklistId: progressData.checklistId, // Store the actual checklist ID
                    isFactoryAssigned: progressData.isFactoryAssigned || false
                  };
                }
                return {
                  ...checklist,
                  progressPercentage: 0,
                  totalQuestions: 0,
                  answeredQuestions: 0,
                  remainingQuestions: 0,
                  actualChecklistId: checklist.checklistId,
                  isFactoryAssigned: false
                };
              } catch (err) {
                console.error(`Error fetching progress for assigned checklist ${checklist.id}:`, err);
                return {
                  ...checklist,
                  progressPercentage: 0,
                  totalQuestions: 0,
                  answeredQuestions: 0,
                  remainingQuestions: 0,
                  actualChecklistId: checklist.checklistId,
                  isFactoryAssigned: false
                };
              }
            })
          );
          
          combinedChecklists = [...combinedChecklists, ...checklistsWithProgress];
        }

        statusStats.total = combinedChecklists.length;
        setStats(statusStats);
        setAllChecklists(combinedChecklists);
        
      } catch (err) {
        console.error('Error fetching checklists:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllChecklists();
  }, [BASE_URL]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get color for progress bars
  const getProgressColor = (percentage, checklistStatus) => {
    if (checklistStatus === 'Completed') return 'success';
    if (percentage === 100) return 'success';
    if (percentage >= 60) return 'primary';
    if (percentage >= 30) return 'info';
    return 'warning';
  };

  // Get filtered checklists based on active tab
  const getFilteredChecklists = () => {
    if (activeTab === 'all') return allChecklists;
    if (activeTab === 'pending') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'pending');
    if (activeTab === 'inProgress') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'in progress');
    if (activeTab === 'completed') return allChecklists.filter(checklist => checklist.status?.toLowerCase() === 'completed');
    return allChecklists;
  };

  const filteredChecklists = getFilteredChecklists();

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <div className="audit-overview-header bg-gradient-primary text-white">
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-1">
                <FaEye className="me-2" />
                Audit Overview
              </h4>
              <p className="mb-0 opacity-75">Monitor all audit checklists across different statuses</p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Stats Cards */}
      <Container fluid className="px-4 py-4">
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-warning-subtle text-warning mb-2">
                  <FaClock size={24} />
                </div>
                <h3 className="fw-bold text-warning">{stats.pending}</h3>
                <p className="text-muted mb-0">Pending</p>
               
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-primary-subtle text-primary mb-2">
                  <FaHourglassHalf size={24} />
                </div>
                <h3 className="fw-bold text-primary">{stats.inProgress}</h3>
                <p className="text-muted mb-0">In Progress</p>
                
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-success-subtle text-success mb-2">
                  <FaCheckCircle size={24} />
                </div>
                <h3 className="fw-bold text-success">{stats.completed}</h3>
                <p className="text-muted mb-0">Completed</p>
                
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3} sm={6} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-info-subtle text-info mb-2">
                  <FaEye size={24} />
                </div>
                <h3 className="fw-bold text-info">{stats.total}</h3>
                <p className="text-muted mb-0">Total Audits</p>
                
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Checklists</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading All Audits</p>
            <p className="text-muted">Please wait...</p>
          </div>
        ) : (
          <>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 pt-4 pb-0 px-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Audit Checklists</h5>
                  
                </div>
                <Tabs
                  activeKey={activeTab}
                  onSelect={handleTabSelect}
                  className="mb-3 nav-tabs-custom"
                >
                  <Tab eventKey="all" title={`All (${stats.total})`} />
                  <Tab eventKey="pending" title={
                    <span>
                      <FaClock className="me-1" size={14} />
                      Pending ({stats.pending})
                    </span>
                  } />
                  <Tab eventKey="inProgress" title={
                    <span>
                      <FaHourglassHalf className="me-1" size={14} />
                      In Progress ({stats.inProgress})
                    </span>
                  } />
                  <Tab eventKey="completed" title={
                    <span>
                      <FaCheckCircle className="me-1" size={14} />
                      Completed ({stats.completed})
                    </span>
                  } />
                </Tabs>
              </Card.Header>
              <Card.Body className="p-4">
                {filteredChecklists.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="empty-state-icon bg-light text-muted mb-3">
                      <FaEye size={32} />
                    </div>
                    <h5>No {activeTab !== 'all' ? activeTab : ''} Audit Checklists Found</h5>
                    <p className="text-muted mb-4">
                      {activeTab === 'all' 
                        ? 'Get started by creating your first audit checklist.'
                        : `No ${activeTab} checklists are available at the moment.`}
                    </p>
                    {activeTab === 'all' && (
                      <Link to="/ChecklistOverview" className="btn btn-primary">
                        Create New Checklist
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>Checklist Name</th>
                          <th>Branch</th>
                          <th>Description</th>
                          <th>Auditor</th>
                          <th>Start Date</th>
                          <th>Status</th>
                          <th>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredChecklists.map((checklist) => (
                          <tr key={`${checklist.id}-${checklist.branchId}-${checklist.status}`}>
                            <td>
                              <div className="fw-medium">{checklist.checklistName}</div>
                            </td>
                            
                            <td>
                              <div className="small text-muted">
                                {checklist.branchName || 'N/A'}
                              </div>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                {checklist.description || 'No description available'}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <FaUser className="text-muted me-2" size={12} />
                                {checklist.auditorName || 'Not assigned'}
                              </div>
                            </td>
                            <td>
                              <div className="small text-muted">
                                {formatDate(checklist.startDate || checklist.createdAt)}
                              </div>
                            </td>
                            <td>
                              <Badge bg={getStatusColor(checklist.status)} className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                                {getStatusIcon(checklist.status)}
                                {checklist.status}
                              </Badge>
                            </td>
                            <td style={{ minWidth: '200px' }}>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small>Progress</small>
                                <Badge 
                                  bg={getProgressColor(checklist.progressPercentage || 0, checklist.status)}
                                >
                                  {checklist.status?.toLowerCase() === 'pending' ? 'Not Started' : `${Math.round(checklist.progressPercentage || 0)}%`}
                                </Badge>
                              </div>
                              <div className="progress" style={{ height: '8px' }}>
                                <div 
                                  className={`progress-bar bg-${getProgressColor(checklist.progressPercentage || 0, checklist.status)}`} 
                                  role="progressbar" 
                                  style={{ width: `${checklist.progressPercentage || 0}%` }}
                                  aria-valuenow={checklist.progressPercentage || 0} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100">
                                </div>
                              </div>
                              <div className="small mt-1 text-muted">
                                {checklist.status?.toLowerCase() === 'pending' ? 
                                  (checklist.totalQuestions ? `Not started - ${checklist.totalQuestions} questions` : 'Not started yet') :
                                  (checklist.totalQuestions && checklist.totalQuestions > 0) ? 
                                    `${checklist.answeredQuestions || 0}/${checklist.totalQuestions} questions` : 
                                    'No data'
                                }
                              </div>
                            </td>
                            
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>

      <style jsx>{`
        .audit-overview-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-bottom-left-radius: 20px;
          border-bottom-right-radius: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }
        
        .empty-state-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin: 0 auto;
        }

        .table th {
          font-weight: 600;
          color: #344767;
          border-bottom: 2px solid #e9ecef;
        }
        
        .progress {
          border-radius: 10px;
          background-color: #f0f2f5;
        }
        
        .progress-bar {
          border-radius: 10px;
        }

        .card {
          transition: transform 0.2s ease-in-out;
        }

        .card:hover {
          transform: translateY(-2px);
        }
        
        .nav-tabs-custom .nav-link {
          font-weight: 600;
          color: #495057;
          border: none;
          border-bottom: 2px solid transparent;
          padding: 0.5rem 1rem;
          margin-right: 1rem;
        }
        
        .nav-tabs-custom .nav-link:hover {
          color: #764ba2;
          border-color: transparent;
        }
        
        .nav-tabs-custom .nav-link.active {
          color: #764ba2;
          border-bottom: 2px solid #764ba2;
          background: transparent;
        }
      `}</style>
    </Container>
  );
}

export default JBranchAuditOverview;